import React, { useState, useEffect } from "react";

const AssessmentParameters = ({ profile, handleChange }) => {
  // State for the parameters
  const [params, setParams] = useState({
    targetQuestionCount: 15,
    difficultyLevel: 3,
    maximumDuration: 45,
  });

  useEffect(() => {
    // Default values
    const defaultParams = {
      targetQuestionCount: profile?.question_count || 15,
      difficultyLevel: profile?.difficulty_level || 3,
      maximumDuration: profile?.duration || 45,
    };
    setParams(defaultParams);
  }, [profile]); // Runs whenever profile changes

  // Get difficulty level description
  const getDifficultyDescription = (level) => {
    switch (level) {
      case 1:
        return "Basic screening with fundamental questions";
      case 2:
        return "Entry-level assessment with some critical thinking";
      case 3:
        return "Intermediate difficulty suitable for mid-level positions";
      case 4:
        return "Advanced assessment for experienced professionals";
      case 5:
        return "Expert-level evaluation for senior or specialized roles";
      default:
        return "Intermediate difficulty suitable for mid-level positions";
    }
  };

  // Get duration description
  const getDurationDescription = (duration) => {
    if (duration <= 20)
      return "Brief assessment focused on key skills (suitable for initial screening)";
    if (duration <= 45)
      return "Standard assessment length with balanced depth (recommended for most roles)";
    if (duration <= 90)
      return "Extended assessment for in-depth evaluation (suitable for senior positions)";
    return "Comprehensive assessment for specialized roles (may impact completion rates)";
  };

  return (
    <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md">
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-primary-50 to-white">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 text-primary-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clipRule="evenodd"
            />
          </svg>
          Assessment Parameters
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Define the assessment length and time constraints
        </p>
      </div>

      <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
          {/* Target Question Count */}
          <div className="rounded-lg p-4 border border-gray-100 hover:shadow-sm transition-all duration-300">
            <div className="flex items-center justify-between">
              <label
                htmlFor="targetQuestionCount"
                className="block text-sm font-medium text-gray-700"
              >
                Target Question Count
                <span className="text-red-600 ml-1">*</span>
              </label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {params.targetQuestionCount} questions
              </span>
            </div>

            <div className="mt-1 relative">
              <input
                type="range"
                min="5"
                max="30"
                className="w-full accent-primary-500"
                value={params.targetQuestionCount}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.question_count = value;
                  handleChange(profile, "parameters");
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 px-1">
                <span>5</span>
                <span>15</span>
                <span>30</span>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Recommended: 10-20 questions for most assessments
            </p>

            {/* Difficulty Level */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Difficulty Level
                </label>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  Level {params.difficultyLevel}
                </span>
              </div>

              <div className="flex items-center">
                <span className="text-xs text-gray-500 mr-2">Basic</span>
                <input
                  type="range"
                  className="flex-grow accent-primary-500"
                  min="1"
                  max="5"
                  step="1"
                  value={params.difficultyLevel}
                  name="screeningParameters[difficultyLevel]"
                  id="difficultyLevel"
                  onChange={(e) => {
                    const value = e.target.value;
                    profile.difficulty_level = value;
                    handleChange(profile, "parameters");
                  }}
                />
                <span className="text-xs text-gray-500 ml-2">Advanced</span>
              </div>

              {/* Difficulty level visualization */}
              <div className="mt-3 flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`h-1.5 rounded flex-1 ${level <= params.difficultyLevel ? "bg-indigo-500" : "bg-gray-300"}`}
                  />
                ))}
              </div>

              <div className="mt-2">
                <p className="text-xs text-gray-600">
                  {getDifficultyDescription(params.difficultyLevel)}
                </p>
              </div>
            </div>
          </div>

          {/* Maximum Duration */}
          <div className="rounded-lg p-4 border border-gray-100 hover:shadow-sm transition-all duration-300">
            <div className="flex items-center justify-between">
              <label
                htmlFor="maximumDuration"
                className="block text-sm font-medium text-gray-700"
              >
                Maximum Duration<span className="text-red-600 ml-1">*</span>
              </label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {params.maximumDuration} minutes
              </span>
            </div>

            <div className="mt-1 relative">
              <input
                type="range"
                id="maximumDuration"
                name="screeningParameters[maximumDuration]"
                min="10"
                max="90"
                className="w-full bg-indigo-500"
                value={params.maximumDuration}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.duration = value;
                  handleChange(profile, "parameters");
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 px-1">
                <span>10</span>
                <span>45</span>
                <span>90</span>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Recommended: 30-45 minutes for comprehensive assessments
            </p>

            {/* Visual representation of duration */}
            <div className="mt-4">
              <h4 className="text-xs font-medium text-gray-500 mb-2">
                Duration Visualization
              </h4>
              <div className="relative">
                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                  <div
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-300 ease-in-out"
                    style={{
                      width: `${Math.min((params.maximumDuration / 90) * 100, 100)}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Brief</span>
                  <span>Standard</span>
                  <span>Extended</span>
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-600">
                {getDurationDescription(params.maximumDuration)}
              </p>
            </div>

            {/* Notice */}
            <div className="mt-4 bg-yellow-50 p-3 rounded-md border border-yellow-100">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    These settings determine the length and complexity of the
                    assessment, but individual job requirements may override
                    them.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentParameters;
